<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';
import { createPropsRestProxy } from '/vue-macros/reactivity-transform/helper';
const props = createPropsRestProxy(__props, ["annotation","pageScale","pageDimensions","textLines","pageNumber","mousePosition"]);

import { useRoute, useRouter } from "vue-router";
import { inject, onMounted, useTemplateRef, watch } from "vue";

import { emptySelection, getDragSelection } from "./get-text-selection";

import { useIsPopout } from "@/hooks/use-is-popout";

import { isRedaction, saveAnnotation } from "@/services/AnnotationService";
import { copyToClipboard, getMarkStatus } from "@/services/UtilitiesService";

import { useMessageStore } from "@/stores/message-store";
import { useUserPermissionStore } from "@/stores/user-permission-store";
import { useCaseSelectionMenuStore } from "@/components/SelectionMenu/case-selection-menu-store";

import type { Ref } from "vue";
import type { TextSelection } from "./get-text-selection";
import type { ParsedMark } from "@/models/Mark";
import type { PageData } from "@/models/PageData";
import type { POS, TextLine } from "@/models/Viewer";
import type { BoundingRectangle } from "@/models/BoundingRectangle";
import type { HighlightAnnotation } from "@/models/Annotation";

type DRAG_STATE = "IDLE" | "HIGHLIGHT";
const emit = defineEmits<{
  (e: "dragEnd"): void;
  (e: "selectPage"): void;
  (e: "reloadAnnotations"): void;
}>();
withDefaults(defineProps<{
  annotation: ParsedMark<HighlightAnnotation>;
  pageScale: number;
  pageDimensions: PageData;
  textLines: TextLine[];
  pageNumber: number;
  mousePosition: { x: number; y: number } | null;
  isCreate?: boolean;
  isSelect?: boolean;
  isDisplay?: boolean;
}>(), {  })
const $route = useRoute();
const $router = useRouter();
const isPopout = useIsPopout();

const selectionMenuStore = useCaseSelectionMenuStore();

let dragState = _ref<DRAG_STATE>("IDLE");
let selectedLines = _ref<BoundingRectangle[]>([]);
let pivot = _ref<POS | null>(null);
// What the drag selects now, and what is saved when it ends. A handle that is let go without moving it has none.
let selection: TextSelection | null = null;

const rotation = inject<Ref<number[]>>("rotation")!;

let publishedSelectionMenuId: string | null;
const selectionGroupRef = useTemplateRef<SVGGElement>("selectedLinesAreaGroup");

onMounted(() => {
  selectedLines.value = [...lines.value];
});

const status = _computed(() => {
  return getMarkStatus(__props.annotation);
});

const style = _computed(() => {
  return `fill: ${markData.value.fillColor}; stroke: ${markData.value.borderColor ?? "none"}; stroke-width: ${markData.value.borderThickness ?? "0px"}; ${
    isRedaction(markData.value) ? "" : `opacity: 0.5`
  }`;
});
const canRedact = _computed(() => {
  return useUserPermissionStore().userPermission.canRedact;
});

const canAnnotate = _computed(() => {
  return useUserPermissionStore().userPermission.canAnnotate || false;
});

const action = _computed(() => {
  if (props.isDisplay) return "READ";
  if (props.isCreate) return "CREATE";
  if (props.isSelect) return "SELECT";
  if (/user|accepted|candidate/.test(status.value) && canAnnotate.value && !isPopout && (!isRedaction(markData.value) || canRedact.value))
    return "EDIT";
  return "READ";
});

const markData = _computed(() => __props.annotation.parsedMarkData);

const pageData = _computed(() => {
  return markData.value.lineGroups[0].pageData;
});

const lines = _computed(() => {
  return markData.value.lineGroups[0].lines;
});

const isSelected = _computed(() => {
  return __props.annotation.markId === $route.params.annotation;
});

const annotationScale = _computed(() => {
  return [90, 270].includes(rotation.value[__props.pageNumber])
    ? __props.pageDimensions.height / pageData.value.height
    : __props.pageDimensions.width / pageData.value.width;
});

const edges = _computed(() => {
  const selected = selectedLines.value.length > 0 ? selectedLines.value : lines.value;
  return selected.length > 0 ? { start: selected[0], end: selected[selected.length - 1] } : null;
});

const boundaries = _computed(() => {
  if (edges.value == null) return [];
  const { start, end } = edges.value;
  return [
    { x: start.x, y: start.y },
    { x: end.x + end.width, y: end.y + end.height },
  ];
});

// A handle holds the other end of the selection at the middle of its edge, not at its corner: a corner is on the
// boundary between two rows, where it is not clear which row it is on.
const pivots = _computed(() => {
  if (edges.value == null) return [];
  const { start, end } = edges.value;
  return [
    { x: start.x, y: start.y + start.height / 2 },
    { x: end.x + end.width, y: end.y + end.height / 2 },
  ];
});

const mp = _computed(() => {
  if (!__props.mousePosition) return __props.mousePosition;
  return {
    x: __props.mousePosition.x / annotationScale.value,
    y: __props.mousePosition.y / annotationScale.value,
  };
});

watch(
  ($route),
  () => {
    if (isSelected.value || dragState.value === "IDLE") return;

    // Navigation interrupts the drag; it is not a completed selection
    dragState.value = "IDLE";
    selectedLines.value = [...lines.value];
  },
  { immediate: true, deep: true },
);

function dragStart(evt: MouseEvent, pos: POS | null) {
  if (!isSelected.value && action.value === "EDIT") return;
  if (props.isSelect) selectedLines.value = [];

  evt.stopPropagation();

  dragState.value = "HIGHLIGHT";
  pivot.value = pos ? { x: pos.x, y: pos.y } : null;
  selection = null;
}

watch((mp), () => {
  if (dragState.value === "IDLE") return;
  if (!isSelected.value && action.value === "EDIT") return;

  selection = getDragSelection(__props.textLines, annotationScale.value, pivot.value!, mp.value!);
  selectedLines.value = selection.lines;
});

async function dragEnd() {
  if (dragState.value === "IDLE") return;
  if (!__props.annotation) return;
  if (!isSelected.value && action.value === "EDIT") return;

  dragState.value = "IDLE";

  const current = selection;
  selection = null;
  // A handle that was let go without moving it has nothing new to save
  if (current == null && action.value === "EDIT") return;
  const { lines: selectedBoxes, startIndex, textLength, selectedText } = current ?? emptySelection();

  if (action.value === "SELECT") {
    const text = selectedText.trim();
    if (text === "") return;

    copyToClipboard(text);

    // Publish Selection
    const anchor = selectionGroupRef.value;
    if (anchor != null) {
      ({ id: publishedSelectionMenuId } = selectionMenuStore.publish({
        source: { kind: "document", fileReference: __props.annotation.fileReference, page: __props.pageNumber, text },
        anchor,
        onDismiss: clearSelection,
      }));
    }
    return;
  }

  if (selectedText.trim() === "") {
    useMessageStore().updateMessage({
      message: "Select some text to create a text mark.",
      type: "warning",
      title: "Could not create annotation",
      autoHide: true,
      showReload: false,
    });
    return;
  }

  const [lineGroup, ...rest] = markData.value.lineGroups;
  await saveAnnotation({
    ...__props.annotation,
    markData: JSON.stringify({
      ...markData.value,
      selectedText,
      lineGroups: [{ ...lineGroup, lines: selectedBoxes, startIndex, textLength }, ...rest],
    }),
  });

  if (action.value === "CREATE") selectAnnotation();
  emit("dragEnd");
  emit("reloadAnnotations");
}

watch(
  () => selectedLines.value,
  (_a, _b, onCleanup) =>
    // whenever the selectedLines change, dismiss the action if existing
    onCleanup(() => {
      if (publishedSelectionMenuId == null) return;
      selectionMenuStore.dismiss(publishedSelectionMenuId);
      publishedSelectionMenuId = null;
    }),
  { immediate: true },
);

function selectAnnotation() {
  if (isSelected.value || isPopout) return;

  const { brand, casename, casefile } = $route.params;

  $router.push({
    name: "casefileannotation",
    params: { brand, casename, casefile, annotation: __props.annotation.markId },
  });
}
function clearSelection() {
  if (dragState.value !== "IDLE" || action.value !== "SELECT") return;

  selectedLines.value = [];
}
</script>
<template>
  <g v-if="markData" :transform="`matrix(${annotationScale}, 0, 0, ${annotationScale}, 0, 0)`" fill-opacity="1">
    <svg
      :height="pageData.height"
      :width="pageData.width"
      :viewBox="`0 0 ${pageData.width} ${pageData.height}`"
      @mouseup="dragEnd"
    >
      <rect
        v-if="dragState === 'HIGHLIGHT'"
        :x="0"
        :y="0"
        :width="pageData.width"
        :height="pageData.height"
        :style="`opacity: 0; cursor: text;`"
      />
      <template v-if="action === 'SELECT'">
        <rect :height="pageData.height" :width="pageData.width" :style="`opacity: 0;`" @click="clearSelection" />
        <rect
          v-for="textLine in textLines"
          :key="textLine.x + '' + textLine.y + '' + textLine.width"
          :x="textLine.x"
          :y="textLine.y - 5"
          :width="textLine.width"
          :height="textLine.height + 10"
          :style="`opacity: 0; cursor: text; fill: blue`"
          @mousedown="dragStart($event, mp)"
        />
        <g ref="selectedLinesAreaGroup">
          <rect
            v-for="line in selectedLines"
            :key="line.x + line.width + line.y"
            :x="line.x"
            :y="line.y"
            :width="line.width"
            :height="line.height"
            :style="style"
            class="tw-cursor-text"
          ></rect>
        </g>
      </template>
      <template v-if="action === 'CREATE'">
        <rect
          v-for="textLine in textLines"
          :key="textLine.x + '' + textLine.y + '' + textLine.width"
          :x="textLine.x"
          :y="textLine.y - 5"
          :width="textLine.width"
          :height="textLine.height + 10"
          :style="`opacity: 0; cursor: text; fill: blue`"
          @mousedown="dragStart($event, mp)"
        />
        <rect
          v-for="line in selectedLines"
          :key="line.x + line.width + line.y"
          :x="line.x"
          :y="line.y"
          :width="line.width"
          :height="line.height"
          :style="style"
          class="tw-cursor-text"
        ></rect>
      </template>
      <template v-if="action === 'EDIT'">
        <g v-if="!isSelected || !boundaries.length">
          <rect
            v-for="line in lines"
            :key="line.x + '' + line.y + '' + line.width"
            :x="line.x"
            :y="line.y"
            :width="line.width"
            :height="line.height"
            :style="style"
            class="tw-cursor-move hover:tw-opacity-50"
            @click="selectAnnotation"
          ></rect>
        </g>
        <g v-else>
          <rect
            v-for="line in selectedLines"
            :key="line.x + line.width + line.y"
            :x="line.x"
            :y="line.y"
            :width="line.width"
            :height="line.height"
            :style="style"
            class="tw-cursor-text"
            @click="selectAnnotation"
          ></rect>
          <circle
            :cx="boundaries[0].x"
            :cy="boundaries[0].y"
            :r="5 / (annotationScale * pageScale)"
            style="cursor: nwse-resize"
            class="tw-fill-blue-600"
            @mousedown="dragStart($event, pivots[1])"
          />
          <circle
            :cx="boundaries[1].x"
            :cy="boundaries[1].y"
            :r="5 / (annotationScale * pageScale)"
            style="cursor: nwse-resize"
            class="tw-fill-blue-600"
            @mousedown="dragStart($event, pivots[0])"
          />
        </g>
      </template>
      <template v-if="action === 'READ'">
        <rect
          v-for="line in lines"
          :key="line.x + '' + line.y + '' + line.width"
          :x="line.x"
          :y="line.y"
          :width="line.width"
          :height="line.height"
          :style="style"
          class="tw-cursor-move hover:tw-opacity-50"
        ></rect>
      </template>
    </svg>
  </g>
</template>
